/**
 * AWS deep dive — org-wide Cost Explorer truth from the `cloud_costs` snapshots.
 *
 * Numbers here are usage-based and come from the management account, so they
 * will not tie out exactly to the invoiced Ramp totals on the Overview tab
 * (see the footnote). Every section stands on its own: the collector backfills
 * forward, and `aws_resource_weekly` in particular stays sparse until the Cost
 * Explorer resource-level preference has been recording for a while.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange, Cloud, Crown, TrendingUp } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  useFinopsAwsForecast,
  useFinopsAwsMonth,
  useFinopsAwsResources,
  useFinopsAwsWeekly,
  useFinopsHistory,
} from '@/hooks/useFinops';
import { AccountOrProjectTable, type EntityRow } from './AccountOrProjectTable';
import { ForecastCard } from './ForecastCard';
import { HistoryStrip, type HistoryGranularity } from './HistoryStrip';
import { ResourceDrawer } from './ResourceDrawer';
import { ServiceTable } from './ServiceTable';
import { WeeklyDigestPanel } from './WeeklyDigestPanel';
import {
  EmptyState, LoaderBlock, StatCard, addDaysISO, deltaOf, fmtDelta, fmtMonth, fmtUSD, fmtWeekRange,
} from './shared';

const ALL_ACCOUNTS = '__all__';
/** 3 months ≈ the 90-day default; each "Load more" pages ~60 days further back. */
const HISTORY_START_MONTHS = 3;
const HISTORY_STEP_MONTHS = 2;
const HISTORY_MAX_MONTHS = 36;

export function FinopsAwsTab() {
  const { t } = useTranslation();

  const [account, setAccount] = useState<string>(ALL_ACCOUNTS);
  const [drawerService, setDrawerService] = useState<string | undefined>();
  const [historyMonths, setHistoryMonths] = useState(HISTORY_START_MONTHS);
  const [granularity, setGranularity] = useState<HistoryGranularity>('month');

  const { data: month, isLoading: monthLoading } = useFinopsAwsMonth();
  const { data: week, isLoading: weekLoading } = useFinopsAwsWeekly();
  // Per-service WoW deltas need the previous week, which only exists once the
  // current week's period is known — hence the deferred second query.
  const priorWeekStart = week ? addDaysISO(week.period, -7) : undefined;
  const { data: priorWeek } = useFinopsAwsWeekly(priorWeekStart, !!priorWeekStart);
  const { data: forecast } = useFinopsAwsForecast();
  const { data: history, isFetching: historyFetching } = useFinopsHistory('aws', historyMonths);

  const drawerOpen = !!drawerService;
  const { data: resources = [], isLoading: resourcesLoading } = useFinopsAwsResources(
    week?.period ?? '',
    drawerService ?? '',
    account === ALL_ACCOUNTS ? undefined : account,
    drawerOpen && !!week?.period,
  );

  const accountRows: EntityRow[] = useMemo(
    () => (month?.accounts ?? []).map((a) => ({ id: a.id, name: a.name, cost: a.cost })),
    [month],
  );

  // Account filter is a client-side fold of the matrix that already travelled.
  const services = useMemo(() => {
    if (!month) return [];
    if (account === ALL_ACCOUNTS) return month.services;
    const byService = new Map<string, number>();
    for (const row of month.matrix) {
      if (row.accountId !== account) continue;
      byService.set(row.service, (byService.get(row.service) ?? 0) + row.cost);
    }
    return Array.from(byService.entries())
      .map(([service, cost]) => ({ service, cost }))
      .sort((a, b) => b.cost - a.cost);
  }, [month, account]);

  if (monthLoading && weekLoading) return <LoaderBlock />;

  const wow = week ? deltaOf(week.total, week.priorTotal) : null;
  // The month view targets the prior calendar month; if it isn't collected yet the
  // reader falls back to the latest month and, past the 5th, flags it (never silently).
  const monthStale = !!month?.stale && !!month.expectedPeriod && month.expectedPeriod !== month.period;
  const accountName = accountRows.find((a) => a.id === account)?.name;
  const canLoadMore = historyMonths < HISTORY_MAX_MONTHS;

  return (
    <div className="space-y-4">
      {/* 1 — Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={CalendarRange}
          tone="blue"
          label={t('finops.aws.stat_prev_month')}
          value={fmtUSD(month?.total)}
          sub={
            month?.period
              ? monthStale
                ? t('finops.aws.month_stale', { period: fmtMonth(month.period), expected: fmtMonth(month.expectedPeriod!) })
                : fmtMonth(month.period)
              : undefined
          }
        />
        <StatCard
          icon={Cloud}
          tone="purple"
          label={t('finops.aws.stat_week')}
          value={fmtUSD(week?.total)}
          sub={week ? `${fmtWeekRange(week.period)} · ${fmtDelta(wow)} ${t('finops.cloud.digest_wow')}` : undefined}
        />
        <StatCard
          icon={TrendingUp}
          tone={
            forecast && forecast.priorMonthActual > 0 && forecast.projected >= forecast.priorMonthActual
              ? 'rose'
              : 'green'
          }
          label={t('finops.aws.stat_projection')}
          value={fmtUSD(forecast?.projected)}
          sub={
            forecast
              ? t('finops.aws.stat_projection_sub', {
                  delta: fmtDelta(deltaOf(forecast.projected, forecast.priorMonthActual)),
                })
              : undefined
          }
        />
        <StatCard
          icon={Crown}
          tone="amber"
          label={t('finops.aws.stat_top_service')}
          value={month?.services[0]?.service ?? '—'}
          sub={month?.services[0] ? fmtUSD(month.services[0].cost) : undefined}
        />
      </div>

      {/* 2 — Weekly digest (hero) + month projection */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <WeeklyDigestPanel
            week={week}
            priorWeek={priorWeek}
            emptyDescription={t('finops.aws.week_empty_description')}
          />
        </div>
        <ForecastCard
          period={forecast?.period}
          projected={forecast?.projected}
          mtdActual={forecast?.mtdActual}
          priorMonthActual={forecast?.priorMonthActual}
          interval={forecast ? { lower: forecast.lower, upper: forecast.upper } : undefined}
          badge={t('finops.aws.forecast_badge')}
          snapshotDate={forecast?.snapshotDate}
          stale={forecast?.stale}
          emptyDescription={t('finops.aws.forecast_empty_description')}
        />
      </div>

      {/* 3 — Previous-month detail */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <h2 className="text-sm font-semibold">
          {t('finops.aws.detail_title')}
          {month?.period && (
            <span className="text-muted-foreground font-normal"> · {fmtMonth(month.period)}</span>
          )}
        </h2>
        <Select value={account} onValueChange={setAccount}>
          <SelectTrigger className="h-8 text-sm w-64">
            <SelectValue placeholder={t('finops.aws.account_filter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ACCOUNTS}>{t('finops.aws.all_accounts')}</SelectItem>
            {accountRows.map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {monthStale && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
          {t('finops.aws.month_stale_banner', { period: fmtMonth(month!.period), expected: fmtMonth(month!.expectedPeriod!) })}
        </p>
      )}

      {!month || month.total === 0 ? (
        <EmptyState
          title={t('finops.cloud.month_empty_title')}
          description={t('finops.aws.month_empty_description')}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AccountOrProjectTable
            title={t('finops.aws.accounts_title')}
            rows={accountRows}
            total={month.total}
            selectedId={account === ALL_ACCOUNTS ? undefined : account}
            onSelect={(id) => setAccount(id ?? ALL_ACCOUNTS)}
            entityColLabel={t('finops.aws.col_account')}
          />
          <ServiceTable
            title={t('finops.cloud.services_title')}
            subtitle={account === ALL_ACCOUNTS ? undefined : accountName ?? account}
            rows={services}
            onOpenResources={week?.period ? setDrawerService : undefined}
          />
        </div>
      )}

      {/* 4 — History */}
      <HistoryStrip
        data={history}
        granularity={granularity}
        onGranularityChange={setGranularity}
        onLoadMore={() => setHistoryMonths((m) => Math.min(HISTORY_MAX_MONTHS, m + HISTORY_STEP_MONTHS))}
        canLoadMore={canLoadMore}
        isLoading={historyFetching}
        emptyDescription={t('finops.aws.history_empty_description')}
      />

      {/* 5 — Footnote */}
      <p className="text-xs text-muted-foreground">{t('finops.cloud.footnote')}</p>

      <ResourceDrawer
        open={drawerOpen}
        onOpenChange={(open) => !open && setDrawerService(undefined)}
        service={drawerService}
        periodLabel={week?.period ? fmtWeekRange(week.period) : undefined}
        rows={resources}
        isLoading={resourcesLoading}
        entityColLabel={t('finops.aws.col_account')}
        emptyDescription={t('finops.aws.resources_empty_description')}
        filterLabel={account === ALL_ACCOUNTS ? undefined : accountName ?? account}
        filterNote={
          account === ALL_ACCOUNTS ? undefined : t('finops.aws.resources_filtered_note')
        }
      />
    </div>
  );
}
